import { assetsClient, type PublicAsset } from '@/clients/assets-client.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';

class HatAssetPublicProfile extends HTMLElement {
    private _assetId: string | null = null;
    private _data: PublicAsset | null = null;
    private _assetTypeLabel: string | null = null;

    static get observedAttributes() {
        return ['asset-id'];
    }

    connectedCallback() {
    this._assetId = this.getAttribute('asset-id');
        if (this._assetId) this._loadData();
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
        if (oldValue === newValue) return;
        if (name === 'asset-id') this._assetId = newValue;
        if (this.isConnected && this._assetId) this._loadData();
    }

    async _loadData() {
        try {
            this._data = await assetsClient.getPublicAsset(this._assetId!);
            if (this._data.assetType) {
                const type = await assetTypesCache.getByCode(this._data.assetType).catch(() => undefined);
                this._assetTypeLabel = type?.description ?? this._data.assetType;
            }
            this._render();
        } catch (err: any) {
            this._renderError();
        }
    }

    _render() {
        if (!this._data) return;

        const { assetType, properties } = this._data;

        this.innerHTML = `
            <style>
                .container {
                    max-width: 640px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                .profile {
                    background: #fff;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
                }

                .asset-type {
                    font-size: 13px;
                    font-weight: 600;
                    color: #6b7280;
                }

                .section {
                    background: #fff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                }

                .section h3 {
                    margin: 0 0 12px;
                    font-size: 15px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .prop-row {
                    display: flex;
                    gap: 12px;
                    padding: 8px 0;
                    border-bottom: 1px solid #f0f0f0;
                    font-size: 14px;
                }

                .prop-row:last-child { border-bottom: none; }

                .prop-label {
                    color: #888;
                    min-width: 140px;
                    flex-shrink: 0;
                    text-transform: capitalize;
                }

                .prop-value { color: #222; }
            </style>

            <div class="container">
                <div class="profile">
                    <div class="asset-type">${this._assetTypeLabel ?? assetType ?? ''}</div>
                </div>

                ${properties?.length ? `
                    <div class="section">
                        <h3>Properties</h3>
                        ${properties.map(p => `
                            <div class="prop-row">
                                <span class="prop-label">${(p.code ?? '').replace(/_/g, ' ').toLowerCase()}</span>
                                <span class="prop-value">${p.value ?? ''}</span>
                            </div>
                        `).join('')}
                    </div>
                ` : ''}
            </div>
        `;
    }

    _renderError() {
        this.innerHTML = `
            <style>
                .error {
                    max-width: 480px;
                    margin: 80px auto;
                    text-align: center;
                    color: #6b7280;
                    font-size: 15px;
                }
            </style>
            <div class="error">Unable to load asset details.</div>
        `;
    }
}

customElements.define('hat-asset-public-profile', HatAssetPublicProfile);